// One-tap favourite Helper toggle. Optimistic UI, falls back on auth redirect.
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { useNavigate } from "@tanstack/react-router";
import { Heart, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { toggleFavorite } from "@/lib/favorites.functions";
import { cn } from "@/lib/utils";

type Props = {
  helperId: string;
  helperName?: string;
  /** Compact circular icon button (used on browse cards). */
  variant?: "icon" | "pill";
  className?: string;
  /** Optional initial state to skip the client check. */
  initialFavorited?: boolean;
};

export function FavoriteButton({ helperId, helperName, variant = "icon", className, initialFavorited }: Props) {
  const navigate = useNavigate();
  const toggle = useServerFn(toggleFavorite);
  const [favorited, setFavorited] = useState<boolean>(!!initialFavorited);
  const [loaded, setLoaded] = useState<boolean>(initialFavorited !== undefined);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (initialFavorited !== undefined) return;
    let cancelled = false;
    (async () => {
      const { data: auth } = await supabase.auth.getUser();
      if (!auth.user) { setLoaded(true); return; }
      const { data } = await supabase
        .from("favorites")
        .select("id")
        .eq("user_id", auth.user.id)
        .eq("helper_id", helperId)
        .maybeSingle();
      if (cancelled) return;
      setFavorited(!!data);
      setLoaded(true);
    })();
    return () => { cancelled = true; };
  }, [helperId, initialFavorited]);

  async function onClick(e: React.MouseEvent) {
    e.preventDefault();
    e.stopPropagation();
    if (busy) return;
    const { data: auth } = await supabase.auth.getUser();
    if (!auth.user) {
      navigate({ to: "/auth", search: { redirect: typeof window !== "undefined" ? window.location.pathname : "/browse" } });
      return;
    }
    setBusy(true);
    const next = !favorited;
    setFavorited(next); // optimistic
    try {
      const res = await toggle({ data: { helperId } });
      setFavorited(res.favorited);
      toast.success(res.favorited ? `Saved ${helperName ?? "Helper"} to favourites` : `Removed from favourites`);
    } catch (err) {
      setFavorited(!next);
      toast.error(err instanceof Error ? err.message : "Couldn't update favourite");
    } finally {
      setBusy(false);
    }
  }

  const label = favorited ? "Remove from favourites" : "Save to favourites";

  if (variant === "pill") {
    return (
      <button
        type="button"
        onClick={onClick}
        aria-pressed={favorited}
        aria-label={label}
        disabled={!loaded || busy}
        className={cn(
          "inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors",
          favorited
            ? "border-rose-200 bg-rose-50 text-rose-700 hover:bg-rose-100 dark:border-rose-900/40 dark:bg-rose-950/30 dark:text-rose-300"
            : "border-border bg-card text-foreground hover:bg-accent",
          className,
        )}
      >
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Heart className={cn("h-3.5 w-3.5", favorited && "fill-current")} />}
        {favorited ? "Favourited" : "Favourite"}
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={favorited}
      aria-label={label}
      title={label}
      disabled={!loaded || busy}
      className={cn(
        "grid h-9 w-9 place-items-center rounded-full border bg-card/95 text-foreground shadow-sm backdrop-blur transition-colors",
        favorited
          ? "border-rose-200 text-rose-600 hover:bg-rose-50 dark:border-rose-900/40 dark:bg-rose-950/30 dark:text-rose-300"
          : "border-border hover:bg-accent",
        className,
      )}
    >
      {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Heart className={cn("h-4 w-4", favorited && "fill-current")} />}
    </button>
  );
}
